'use client';

import { useCallback, useRef } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';

/**
 * Returns a stable callback that strips the supplied query params from the
 * current URL via `router.replace` (no navigation / scroll, no extra render
 * beyond the normal search-params update).
 *
 * Intended for "one-shot redirect" params such as `?tab=sales&secondaryTab=invoices`
 * that should be consumed once and then removed so that a page reload does not
 * re-trigger the redirect. The caller decides WHEN to clear (e.g. on user
 * navigation) — this hook only provides the clearing primitive.
 *
 * If none of the given params are currently present, the call is a no-op.
 */
export function useClearRedirectParams(paramNames: string[]) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Keep the latest param list in a ref so the returned callback identity is
  // stable across re-renders even when the caller passes an inline array.
  const paramsRef = useRef<string[]>(paramNames);
  paramsRef.current = paramNames;

  return useCallback(() => {
    const current = new URLSearchParams(searchParams.toString());
    let changed = false;
    for (const name of paramsRef.current) {
      if (current.has(name)) {
        current.delete(name);
        changed = true;
      }
    }
    if (!changed) return;
    const qs = current.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [router, pathname, searchParams]);
}
